import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  useCategoryTree,
  useCategories,
  type CategoryNode,
} from '../hooks/useCategories';
import {
  useBudgetSummary,
  useSetBudget,
  type BudgetSummaryItem,
} from '../hooks/useBudgets';
import {
  useMonthNavigation,
  getPreviousMonth,
} from '../hooks/useMonthNavigation';
import { ProgressBar } from '../components/ui/ProgressBar';
import { MonthNavigator } from '../components/ui/MonthNavigator';
import { formatCurrency, calculatePercentUsed } from '../utils/format';
import { getProgressVariant, PROGRESS_COLORS } from '../utils/constants';

interface EditableCellProps {
  value: number;
  onSave: (value: number) => void;
  lastMonthValue?: number;
}

function EditableCell({ value, onSave, lastMonthValue }: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());

  const handleClick = () => {
    setEditValue(value.toString());
    setIsEditing(true);
  };

  const handleBlur = () => {
    const parsed = parseFloat(editValue);
    if (!isNaN(parsed) && parsed >= 0) {
      onSave(parsed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  const handleCopyLastMonth = (e: React.MouseEvent) => {
    e.preventDefault();
    if (lastMonthValue !== undefined) {
      setEditValue(lastMonthValue.toString());
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1 justify-end">
        <input
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-24 px-2 py-1 border border-brand-primary rounded text-right focus:outline-none focus:ring-2 focus:ring-brand-secondary"
          autoFocus
          min="0"
          step="100"
        />
        <button
          type="button"
          onClick={handleCopyLastMonth}
          onMouseDown={(e) => e.preventDefault()}
          disabled={!lastMonthValue || lastMonthValue === 0}
          className={`p-1 rounded transition-colors ${
            lastMonthValue && lastMonthValue > 0
              ? 'text-mid-gray hover:text-brand-primary hover:bg-brand-secondary/10'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title={
            lastMonthValue && lastMonthValue > 0
              ? `Usar valor del mes anterior: ${formatCurrency(lastMonthValue)}`
              : 'No hay valor del mes anterior'
          }
          aria-label="Copiar valor del mes anterior"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-5 h-5"
          >
            <path
              fillRule="evenodd"
              d="M7.793 2.232a.75.75 0 01-.025 1.06L3.622 7.25h10.003a5.375 5.375 0 010 10.75H10.75a.75.75 0 010-1.5h2.875a3.875 3.875 0 000-7.75H3.622l4.146 3.957a.75.75 0 01-1.036 1.085l-5.5-5.25a.75.75 0 010-1.085l5.5-5.25a.75.75 0 011.06.025z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="px-2 py-1 hover:bg-brand-secondary/10 rounded cursor-pointer text-brand-primary font-medium"
      title="Clic para editar"
    >
      [{formatCurrency(value)}]
    </button>
  );
}

interface CategoryGroupProps {
  category: CategoryNode;
  budgetMap: Map<string, BudgetSummaryItem>;
  lastMonthBudgetMap: Map<string, BudgetSummaryItem>;
  expandedGroups: Set<string>;
  onToggleGroup: (id: string) => void;
  onUpdateBudget: (categoryId: string, estimated: number) => void;
  currentMonth: string;
}

function CategoryGroup({
  category,
  budgetMap,
  lastMonthBudgetMap,
  expandedGroups,
  onToggleGroup,
  onUpdateBudget,
  currentMonth,
}: CategoryGroupProps) {
  const isExpanded = expandedGroups.has(category.id);
  const hasChildren = category.children.length > 0;
  const budgetItem = budgetMap.get(category.id);

  const getTransactionsLink = (categoryId: string) =>
    `/transacciones?month=${currentMonth}&category=${categoryId}`;

  const groupEstimated = useMemo(() => {
    let sum = budgetItem?.estimated ?? 0;
    category.children.forEach((child) => {
      const childBudget = budgetMap.get(child.id);
      sum += childBudget?.estimated ?? 0;
    });
    return sum;
  }, [category.children, budgetMap, budgetItem]);

  const groupActual = useMemo(() => {
    let sum = budgetItem?.actual ?? 0;
    category.children.forEach((child) => {
      const childBudget = budgetMap.get(child.id);
      sum += childBudget?.actual ?? 0;
    });
    return sum;
  }, [category.children, budgetMap, budgetItem]);

  return (
    <>
      <tr
        className={`border-b border-light-gray hover:bg-near-white bg-gray-50`}
      >
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            {hasChildren ? (
              <button
                onClick={() => onToggleGroup(category.id)}
                className="w-5 h-5 flex items-center justify-center text-mid-gray hover:text-near-black transition-colors"
                aria-label={isExpanded ? 'Contraer' : 'Expandir'}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            ) : (
              <span className="w-5" />
            )}
            {category.icon && <span className="text-lg">{category.icon}</span>}
            <span
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: category.color }}
            />
            <span className="font-semibold">{category.name}</span>
            {!isExpanded && hasChildren && (
              <span className="text-sm text-mid-gray ml-2">(colapsado)</span>
            )}
          </div>
        </td>
        <td className="py-3 px-4 text-right">
          {isExpanded && hasChildren ? null : isExpanded ? (
            <EditableCell
              value={budgetItem?.estimated ?? 0}
              onSave={(value) => onUpdateBudget(category.id, value)}
              lastMonthValue={lastMonthBudgetMap.get(category.id)?.estimated}
            />
          ) : (
            <span className="font-medium">
              {formatCurrency(groupEstimated)}
            </span>
          )}
        </td>
        <td className="py-3 px-4 text-right">
          {isExpanded && hasChildren ? null : isExpanded ? (
            <Link
              to={getTransactionsLink(category.id)}
              className={`hover:underline ${
                (budgetItem?.actual ?? 0) > (budgetItem?.estimated ?? 0) &&
                (budgetItem?.estimated ?? 0) > 0
                  ? 'text-red-600 font-semibold'
                  : 'text-brand-primary'
              }`}
              title="Ver transacciones"
            >
              {formatCurrency(budgetItem?.actual ?? 0)}
            </Link>
          ) : (
            <Link
              to={getTransactionsLink(category.id)}
              className={`hover:underline ${
                groupActual > groupEstimated && groupEstimated > 0
                  ? 'text-red-600 font-semibold'
                  : 'text-brand-primary'
              }`}
              title="Ver transacciones"
            >
              {formatCurrency(groupActual)}
            </Link>
          )}
        </td>
        <td className="py-3 px-4">
          {isExpanded && hasChildren ? null : isExpanded ? (
            <div className="flex items-center gap-2">
              <ProgressBar
                percentUsed={calculatePercentUsed(
                  budgetItem?.actual ?? 0,
                  budgetItem?.estimated ?? 0
                )}
              />
              {(budgetItem?.actual ?? 0) > (budgetItem?.estimated ?? 0) &&
                (budgetItem?.estimated ?? 0) > 0 && (
                  <span title="Sobre presupuesto" className="text-red-500">
                    ⚠️
                  </span>
                )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <ProgressBar
                percentUsed={calculatePercentUsed(groupActual, groupEstimated)}
              />
              {groupActual > groupEstimated && groupEstimated > 0 && (
                <span title="Sobre presupuesto" className="text-red-500">
                  ⚠️
                </span>
              )}
            </div>
          )}
        </td>
      </tr>
      {isExpanded &&
        category.children.map((child) => {
          const childBudget = budgetMap.get(child.id);
          const childActual = childBudget?.actual ?? 0;
          const childEstimated = childBudget?.estimated ?? 0;
          const isOverBudget =
            childActual > childEstimated && childEstimated > 0;

          return (
            <tr
              key={child.id}
              className="border-b border-light-gray/50 hover:bg-near-white"
            >
              <td className="py-2 px-4">
                <div className="flex items-center gap-2 pl-7">
                  <span className="text-mid-gray text-sm">├──</span>
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: child.color }}
                  />
                  <span className="text-sm">{child.name}</span>
                </div>
              </td>
              <td className="py-2 px-4 text-right">
                <EditableCell
                  value={childEstimated}
                  onSave={(value) => onUpdateBudget(child.id, value)}
                  lastMonthValue={lastMonthBudgetMap.get(child.id)?.estimated}
                />
              </td>
              <td className="py-2 px-4 text-right">
                <Link
                  to={getTransactionsLink(child.id)}
                  className={`hover:underline text-sm ${
                    isOverBudget
                      ? 'text-red-600 font-semibold'
                      : 'text-brand-primary'
                  }`}
                  title="Ver transacciones"
                >
                  {formatCurrency(childActual)}
                </Link>
              </td>
              <td className="py-2 px-4">
                <div className="flex items-center gap-2">
                  <ProgressBar
                    percentUsed={calculatePercentUsed(
                      childActual,
                      childEstimated
                    )}
                  />
                  {isOverBudget && (
                    <span title="Sobre presupuesto" className="text-red-500">
                      ⚠️
                    </span>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      {isExpanded && hasChildren && (
        <tr className="border-b border-light-gray bg-gray-100">
          <td
            className="py-2 px-4 text-right text-sm text-mid-gray"
            colSpan={1}
          >
            Subtotal
          </td>
          <td className="py-2 px-4 text-right font-medium text-sm">
            {formatCurrency(groupEstimated)}
          </td>
          <td className="py-2 px-4 text-right font-medium text-sm">
            <Link
              to={getTransactionsLink(category.id)}
              className="text-brand-primary hover:underline"
              title="Ver transacciones de esta categoría"
            >
              {formatCurrency(groupActual)}
            </Link>
          </td>
          <td className="py-2 px-4" />
        </tr>
      )}
    </>
  );
}

export function BudgetPage() {
  const categoryTree = useCategoryTree();
  const categories = useCategories();
  const setBudget = useSetBudget();

  const {
    currentMonth,
    goToPrevMonth: handlePrevMonth,
    goToNextMonth: handleNextMonth,
  } = useMonthNavigation();

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (categoryTree.length > 0 && expandedGroups.size === 0) {
      setExpandedGroups(new Set(categoryTree.map((cat) => cat.id)));
    }
  }, [categoryTree, expandedGroups.size]);

  const { items: budgetItems, totals } = useBudgetSummary(currentMonth);
  const previousMonth = useMemo(
    () => getPreviousMonth(currentMonth),
    [currentMonth]
  );
  const { items: lastMonthBudgetItems } = useBudgetSummary(previousMonth);

  const budgetMap = useMemo(() => {
    const map = new Map<string, BudgetSummaryItem>();
    budgetItems.forEach((item) => {
      map.set(item.categoryId, item);
    });
    return map;
  }, [budgetItems]);

  const lastMonthBudgetMap = useMemo(() => {
    const map = new Map<string, BudgetSummaryItem>();
    lastMonthBudgetItems.forEach((item) => {
      map.set(item.categoryId, item);
    });
    return map;
  }, [lastMonthBudgetItems]);

  const handleToggleGroup = useCallback((id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleUpdateBudget = useCallback(
    (categoryId: string, estimated: number) => {
      setBudget({
        categoryId,
        month: currentMonth,
        estimated,
      });
    },
    [setBudget, currentMonth]
  );

  const rootCategories = useMemo(() => {
    return categoryTree.filter((cat) => cat.parentId === null);
  }, [categoryTree]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-brand-primary">
          Presupuesto
        </h1>
        <MonthNavigator
          currentMonth={currentMonth}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
        />
      </div>

      {categories.length === 0 ? (
        <div className="text-center py-12 text-mid-gray">
          <p className="text-lg mb-2">No hay categorías configuradas</p>
          <p className="text-sm">
            Importa datos o configura categorías para comenzar a presupuestar.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="w-full min-w-[500px]">
            <thead>
              <tr className="bg-brand-primary text-white">
                <th className="py-3 px-4 text-left font-medium">Categoría</th>
                <th className="py-3 px-4 text-right font-medium">Estimado</th>
                <th className="py-3 px-4 text-right font-medium">Real</th>
                <th className="py-3 px-4 text-left font-medium">Progreso</th>
              </tr>
            </thead>
            <tbody>
              {rootCategories.map((category) => (
                <CategoryGroup
                  key={category.id}
                  category={category}
                  budgetMap={budgetMap}
                  lastMonthBudgetMap={lastMonthBudgetMap}
                  expandedGroups={expandedGroups}
                  onToggleGroup={handleToggleGroup}
                  onUpdateBudget={handleUpdateBudget}
                  currentMonth={currentMonth}
                />
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-near-black text-white font-semibold">
                <td className="py-4 px-4">TOTAL</td>
                <td className="py-4 px-4 text-right">
                  {formatCurrency(totals.estimated)}
                </td>
                <td className="py-4 px-4 text-right">
                  {formatCurrency(totals.actual)}
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-4 bg-gray-600 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${PROGRESS_COLORS[getProgressVariant(totals.percentUsed)].bar} transition-all duration-300`}
                        style={{
                          width: `${Math.min(totals.percentUsed, 100)}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm">
                      {totals.percentUsed.toFixed(0)}%
                    </span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
